




































import { Vue, Component, InjectReactive, Prop } from 'vue-property-decorator';
import { inject } from 'inversify-props';
import ConversationUserPermissionsModel from '@/models/crm/conversation-user-permissions.model';
import { InjectionIdEnum } from '@/enums/injection-id.enum';
import ConversationService from '@/services/crm/conversation.service';
import { ConversationContactStatusEnum } from '@/enums/crm/conversation-contact-status.enum';
import ContactModel from '@/models/crm/contact.model';
import { ICreateConversation } from '@/interfaces/crm/create-conversation.interface';
import SettingsService from '@/services/crm/settings.service';
import SettingsModel from '@/models/crm/settings.model';
import SessionService from '@/services/session.service';
import UserContactInfo from '@/models/crm/user-contact-info.model';
import EvoTalksFilaAtendenteModel from '@/models/crm/evotalks-fila-atendente.model';
import EvoTalksSyncingDialog from '@/components/crm/evotalks-syncing-dialog.vue';
import EvoTalksQueueSelectDialog from '@/components/crm/evotalks-queue-select-dialog.vue';
import { IEvoTalksTemplateSend } from '@/utils/helpers/evotalks-template-helper';
import { ISharedWindow, openSharedWindow } from '@/utils/helpers/shared-window-helper';
import CRMEvoTalksService from '@/services/crm/evotalks.service';

@Component({
  components: {
    EvoTalksSyncingDialog,
    EvoTalksQueueSelectDialog,
  },
})
export default class CrmChatCallerButton extends Vue {
  @inject(InjectionIdEnum.CrmConversationService)
  private conversationService!: ConversationService;

  @inject(InjectionIdEnum.CRMEvoTalksService)
  private evotalksService!: CRMEvoTalksService;

  @inject(InjectionIdEnum.CrmSettingsService)
  private settingsService!: SettingsService;

  @inject(InjectionIdEnum.SessionService)
  private sessionService!: SessionService;

  @InjectReactive('conversationUserPermission') readonly conversationUserPermission!: ConversationUserPermissionsModel;

  @InjectReactive('userContactInfo') readonly userContactInfo!: UserContactInfo;

  @Prop({ required: true })
  contactNumber!: string;

  @Prop()
  clientId!: string;

  @Prop()
  contact!: ContactModel;

  @Prop()
  width!: boolean | string;

  @Prop()
  height!: boolean | string;

  @Prop()
  color!: string;

  @Prop({ default: false })
  disabled!: boolean;

  @Prop()
  nameButton!: string;

  @Prop({ default: true })
  fab!: boolean;

  @Prop({ default: 'crm.chatCallerButton.messages.InactiveContact' })
  tooltipDisabled!: string;

  /**
   * 'auto' mantem o comportamento historico (EvoTalks quando configurado, senao GeoChat).
   * 'evotalks'/'geochat' fixam o destino — usado quando a tela mostra os dois botoes lado a lado.
   */
  @Prop({ default: 'auto' })
  target!: 'auto' | 'evotalks' | 'geochat';

  loading = false;

  syncingEvoTalks = false;

  queueDialog = false;

  sendingTemplate = false;

  private settings!: SettingsModel;

  async mounted(): Promise<void> {
    this.settings = await this.settingsService.getSettings();
  }

  get evoTalksQueues(): EvoTalksFilaAtendenteModel[] {
    return this.userContactInfo?.evoTalksQueues ?? [];
  }

  async onClick(): Promise<void> {
    const evoTalksConfigured = !!(this.settings.flagUtilizaEvoTalks && this.settings.urlBaseEvoTalks);
    if (this.target === 'evotalks' && !evoTalksConfigured) {
      return;
    }
    if (this.target !== 'geochat' && evoTalksConfigured) {
      const queues = this.evoTalksQueues;
      if (queues.length === 0) {
        this.$notify.warn(this.$t('crm.chatCallerButton.messages.noQueueAvailable'), 8000);
        return;
      }
      this.queueDialog = true;
      return;
    }

    this.loading = true;
    try {
      if (this.canOpenConversation()) {
        const normalizedNumber = this.normalizeContactNumber();
        const checkResult = await this.conversationService.checkContactNumber(normalizedNumber);
        const validWaContact = checkResult.find((x) => x.status === ConversationContactStatusEnum.Valid);

        if (validWaContact) {
          const payload: ICreateConversation = {
            contact: this.contact,
            waContact: validWaContact,
          };
          const { apiToken, apiUrl, socketIOUrl } = this.sessionService;
          this.$store.commit('setContactNumber', normalizedNumber);
          this.$store.commit('setClientId', this.clientId);
          this.$store.commit('setCreateConversationPayload', payload);
          this.$store.commit('setUserContactInfo', this.userContactInfo);

          const chatWindowName = 'GeoChatWindow';
          const chatWindowUrl = this.$router.resolve({
            name: 'GeoChatView',
            query: {
              token: apiToken,
              apiUrl,
              socketIOUrl,
            },
          }).href;

          let chatWindow = window.open('', chatWindowName);

          if (!chatWindow || chatWindow.closed) {
            chatWindow = window.open(chatWindowUrl, chatWindowName);
          } else {
            chatWindow.location.href = chatWindowUrl;
            chatWindow.focus();
          }
          this.loading = false;
        } else {
          this.$notify.warn(
            this.$t('crm.chatCallerButton.messages.couldNotFoundAValidWaIdForNumber', {
              contactNumber: normalizedNumber,
            }),
            5000,
          );
        }
      }
    } catch (err) {
      this.$notify.error(err && (err as Error).message);
    } finally {
      this.loading = false;
    }
  }

  async onOpenChat(queue: EvoTalksFilaAtendenteModel): Promise<void> {
    const janela = await openSharedWindow();
    this.queueDialog = false;
    await this.abrirEvoTalks(janela, queue);
  }

  /**
   * O dialog so fecha quando a conversa abre: em falha o atendente corrige e tenta de novo.
   */
  async onSendTemplate(payload: IEvoTalksTemplateSend): Promise<void> {
    const janela = await openSharedWindow();
    this.sendingTemplate = true;
    try {
      const result = await this.evotalksService.checkTemplate(
        payload.queue.evoQueueId,
        payload.templateId,
        this.contactNumber,
        payload.values,
      );
      if (result.openChat?.owner === 'OTHER') {
        janela.close();
        this.$notify.warn(this.$t('crm.chatCallerButton.templateMessages.chatWithOtherAttendant'), 8000);
        return;
      }
      if (!result.ready) {
        this.$notify.warn(this.$t('crm.chatCallerButton.templateMessages.chatAlreadyOpen'), 8000);
      }
      this.queueDialog = false;
      await this.abrirEvoTalks(janela, payload.queue, result.ready ? payload : undefined);
    } catch (err) {
      janela.close();
      this.$notify.error((err as Error)?.message || this.$t('crm.chatCallerButton.templateMessages.sendError'));
    } finally {
      this.sendingTemplate = false;
    }
  }

  /**
   * A janela vem aberta do gesto do clique: window.open depois de um await e bloqueado pelo
   * navegador em silencio. Quem envia o template e o proprio link (t/tp): e o unico caminho da
   * EvoTalks que abre o atendimento ja fixado no contato (c). Sem p: o numero ja vai com o 55, e
   * o p declara numero na forma nacional.
   */
  private async abrirEvoTalks(
    janela: ISharedWindow,
    queue: EvoTalksFilaAtendenteModel,
    template?: IEvoTalksTemplateSend,
  ): Promise<void> {
    this.loading = true;
    try {
      const normalizedNumber = this.normalizeContactNumber();
      const evoTalksUrl = new URL('/base/agentchat', this.settings.urlBaseEvoTalks);
      const evoClientId = await this.garantirSincronizacaoEvoTalks();
      evoTalksUrl.searchParams.set('q', queue.evoQueueId);
      if (evoClientId) {
        evoTalksUrl.searchParams.set('c', evoClientId);
      }
      evoTalksUrl.searchParams.set('n', normalizedNumber.replace(/\D/g, ''));
      if (template) {
        evoTalksUrl.searchParams.set('t', String(template.templateId));
        evoTalksUrl.searchParams.set('tp', JSON.stringify(template.values));
        evoTalksUrl.searchParams.set('who', '0');
      }
      if (!janela.navigate(evoTalksUrl.toString())) {
        this.$notify.warn(this.$t('crm.chatCallerButton.messages.popupBlocked'), 8000);
      }
    } catch (err) {
      this.$notify.error(err && (err as Error).message);
    } finally {
      this.loading = false;
    }
  }

  get showButton(): boolean {
    return !!this.conversationUserPermission;
  }

  get filterTooltipMessage(): string {
    return `${this.$t(this.tooltipDisabled)}`;
  }

  private normalizeContactNumber():string {
    const onlyNumbers = this.contactNumber.replace(/[^0-9]/g, '');

    const numbersWith55 = (onlyNumbers.length === 10 || onlyNumbers.length === 11)
      ? `55${onlyNumbers}`
      : onlyNumbers;

    if (numbersWith55.length === 13) {
      return numbersWith55.replace(/(\d{2})(\d{2})(\d{5})(\d{4})/, '+$1 ($2) $3-$4');
    }

    if (numbersWith55.length === 12) {
      return numbersWith55.replace(/(\d{2})(\d{2})(\d{4})(\d{4})/, '+$1 ($2) $3-$4');
    }

    return numbersWith55;
  }

  /**
   * Garante contato + empresa no EvoTalks e devolve o id do contato la (o `c` do deep link), ou
   * null quando nao foi possivel identificar: a conversa abre mesmo assim, so pelo numero.
   */
  private async garantirSincronizacaoEvoTalks(): Promise<string | null> {
    // Contato sintetico do prospect (id=-1, sem registro): sincroniza a EMPRESA on-demand
    if (this.contact && (!this.contact.id || this.contact.id <= 0)) {
      return this.garantirSincronizacaoProspect();
    }
    if (!this.contact || !this.contact.id) {
      return null;
    }
    if (this.contact.evoClientId) {
      return this.contact.evoClientId;
    }
    // So pula a sincronizacao quando o status CONFIRMA que contato + empresa ja estao
    // sincronizados. Se a leitura do status falhar, tenta sincronizar mesmo assim
    // (best-effort) — nao pular silenciosamente.
    try {
      const status = await this.conversationService.getEvoTalksSyncStatus(this.contact.id);
      if (!status.evoTalksAtivo) {
        return null;
      }
      if (status.contactSynced && status.companySynced) {
        return status.evoClientId ?? null;
      }
    } catch (err) {
      // status indisponivel: segue para a tentativa de sincronizacao
    }
    this.syncingEvoTalks = true;
    try {
      const result = await this.conversationService.ensureEvoTalksSync(this.contact.id);
      if (!result.contactSynced || !result.companySynced) {
        this.$notify.warn(this.$t('crm.chatCallerButton.messages.evoTalksSyncIncomplete'), 5000);
      }
      return result.evoClientId ?? null;
    } catch (err) {
      // sync on-demand nao pode impedir a conversa: segue para o EvoTalks mesmo em falha
      this.$notify.warn(this.$t('crm.chatCallerButton.messages.evoTalksSyncIncomplete'), 5000);
      return null;
    } finally {
      this.syncingEvoTalks = false;
    }
  }

  private async garantirSincronizacaoProspect(): Promise<string | null> {
    const prospectId = this.contact?.idProspect;
    if (!prospectId) {
      return null;
    }
    // So pula quando o status CONFIRMA empresa sincronizada; falha na leitura -> tenta sincronizar
    try {
      const status = await this.evotalksService.getStatus(null, prospectId);
      if (status.empresaSincronizada) {
        return status.evoClientId ?? null;
      }
    } catch (err) {
      // status indisponivel: segue para a tentativa de sincronizacao
    }
    this.syncingEvoTalks = true;
    try {
      const result = await this.evotalksService.syncEmpresa(null, prospectId);
      if (result.empresaSincronizada) {
        await this.evotalksService.syncContatos(null, prospectId);
      } else {
        this.$notify.warn(this.$t('crm.chatCallerButton.messages.evoTalksSyncIncomplete'), 5000);
      }
      return result.evoClientId ?? null;
    } catch (err) {
      this.$notify.warn(this.$t('crm.chatCallerButton.messages.evoTalksSyncIncomplete'), 5000);
      return null;
    } finally {
      this.syncingEvoTalks = false;
    }
  }

  private canOpenConversation(): boolean {
    if (!this.conversationUserPermission) {
      this.$notify.error(this.$t('crm.chatCallerButton.messages.errorOnGetPermissions'), 5000);
      return false;
    }

    if (!this.conversationUserPermission.viewWhatsApp) {
      this.$notify.warn(this.$t('crm.chatCallerButton.messages.whatsappDisabled'), 5000);
      return false;
    }

    if (!this.conversationUserPermission.manageWhatsApp) {
      this.$notify.warn(this.$t('crm.chatCallerButton.messages.youDoNotHavePermissiontoManageWhatsApp'), 5000);
      return false;
    }

    return true;
  }
}
